/**
 * OpenTelemetry initialization for grizzly-web SPA.
 *
 * Sends traces directly to Honeycomb via OTLP HTTP (no backend relay needed).
 * Uses a write-only ingest key that is safe to expose in browser code.
 *
 * Instruments:
 * - Fetch (all API calls) via auto-instrumentation
 * - View transitions via manual spans
 * - Core Web Vitals via otel-vitals.ts
 * - Error boundaries via otel-vitals.ts
 *
 * Tenet 0: telemetry must never crash the SPA or alter its behaviour.
 */

import { trace, SpanKind, SpanStatusCode, type Span } from '@opentelemetry/api'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations, type Instrumentation } from '@opentelemetry/instrumentation'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'
import { initWebVitals } from './otel-vitals.js'

// Re-export error boundary helpers
export { withErrorBoundary, reportUnhandledError } from './otel-vitals.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OTelConfig {
  /** Honeycomb ingest key (write-only, injected via <meta> tag). */
  honeycombApiKey: string | undefined
  /** Honeycomb dataset name (default: 'grizzly-web'). */
  honeycombDataset?: string
  /** Service name (default: 'grizzly-web'). */
  serviceName?: string
}

/** Honeycomb EU OTLP endpoint. */
const HONEYCOMB_EU_ENDPOINT = 'https://api.eu1.honeycomb.io'

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

let _provider: WebTracerProvider | null = null
let _tracer: ReturnType<WebTracerProvider['getTracer']> | null = null
let _initialized = false

// ---------------------------------------------------------------------------
// Core init
// ---------------------------------------------------------------------------

/**
 * Initialize the OTel WebTracerProvider.
 *
 * If `honeycombApiKey` is set, sends traces directly to Honeycomb (no relay).
 * Otherwise falls back to the backend relay endpoint.
 *
 * Tenet 0: telemetry failures must never crash the SPA.
 */
export function initOtel(config: OTelConfig): void {
  if (_initialized) {
    return
  }

  const serviceName = config.serviceName ?? 'grizzly-web'

  try {
    const exporter = createExporter(config)
    if (!exporter) {
      _initialized = true
      return
    }

    _provider = new WebTracerProvider({
      spanProcessors: [new BatchSpanProcessor(exporter)],
    })

    trace.setGlobalTracerProvider(_provider)

    const instrumentations: Instrumentation[] = getWebAutoInstrumentations({
      '@opentelemetry/instrumentation-fetch': { enabled: true },
      '@opentelemetry/instrumentation-xml-http-request': { enabled: false },
    })
    registerInstrumentations({ instrumentations })

    _tracer = _provider.getTracer(serviceName)
    _initialized = true

    initWebVitals()
  } catch {
    _provider = null
    _tracer = null
    _initialized = true
  }
}

function createExporter(config: OTelConfig): OTLPTraceExporter | null {
  if (!config.honeycombApiKey) {
    // No ingest key → telemetry disabled (no fallback)
    return null
  }
  return new OTLPTraceExporter({
    url: `${HONEYCOMB_EU_ENDPOINT}/v1/traces`,
    headers: {
      'x-honeycomb-team': config.honeycombApiKey,
      'x-honeycomb-dataset': config.honeycombDataset ?? 'grizzly-web',
    },
  })
}

// ---------------------------------------------------------------------------
// Convenience helpers
// ---------------------------------------------------------------------------

export function startViewSpan(viewName: string, route: string): Span | null {
  if (!_tracer) {
    return null
  }
  const span = _tracer.startSpan(`web.view.${viewName}`, {
    kind: SpanKind.CLIENT,
    attributes: { 'grizzly.web.view': viewName, 'grizzly.web.route': route },
  })
  const endOnNav = () => {
    span.end()
    window.removeEventListener('hashchange', endOnNav)
  }
  window.addEventListener('hashchange', endOnNav)
  return span
}

export function startChildSpan<T>(
  name: string,
  fn: () => T,
  attributes?: Record<string, string>,
): T {
  if (!_tracer) {
    return fn()
  }
  return _tracer.startActiveSpan(name, (span: Span) => {
    if (attributes) {
      Object.entries(attributes).forEach(([k, v]) => span.setAttribute(k, v))
    }
    try {
      return fn()
    } finally {
      span.end()
    }
  })
}

export function recordError(message: string): void {
  const span = trace.getActiveSpan()
  if (!span) {
    return
  }
  span.recordException(new Error(message))
  span.setStatus({ code: SpanStatusCode.ERROR, message })
  span.end()
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

export async function flushOtel(): Promise<void> {
  if (!_provider) {
    return
  }
  try {
    await _provider.forceFlush()
  } catch {
    // Tenet 0
  }
}
